import SDK from "../../Components/SDK";
import { getStoreCollections } from "./storeData";

/** Values are the `type` query param the backend expects — note "recent", not "recents". */
export const GLOBAL_SEARCH_TYPE = {
    RECENTS: "recent",
    CONTACTS: "contacts",
    GROUPS: "groups",
    MESSAGES: "messages"
};

export const GLOBAL_SEARCH_PAGE_SIZE = 20;

/**
 * Message matches are capped per search term (business rule 4). Contacts and groups
 * are explicitly uncapped, so this applies to the messages tab only.
 */
export const GLOBAL_SEARCH_MESSAGE_CAP = 1000;

const RESPONSE_KEY_BY_TYPE = {
    [GLOBAL_SEARCH_TYPE.RECENTS]: "recentChats",
    [GLOBAL_SEARCH_TYPE.CONTACTS]: "contacts",
    [GLOBAL_SEARCH_TYPE.GROUPS]: "groups",
    [GLOBAL_SEARCH_TYPE.MESSAGES]: "messages"
};

const SEARCHABLE_FIELDS_BY_TYPE = {
    [GLOBAL_SEARCH_TYPE.RECENTS]: ["fromUserName", "toUserName", "participantName"],
    [GLOBAL_SEARCH_TYPE.CONTACTS]: ["nickname"],
    [GLOBAL_SEARCH_TYPE.GROUPS]: ["groupName"],
    [GLOBAL_SEARCH_TYPE.MESSAGES]: ["message"]
};

const matchesSearch = (item, type, searchText) => {
    const term = searchText.toLowerCase();
    return (SEARCHABLE_FIELDS_BY_TYPE[type] || []).some((field) =>
        String(item?.[field] || "").toLowerCase().includes(term)
    );
};

/**
 * Calls the SDK's global search. The SDK owns the endpoint URL, the auth token and its
 * refresh, and the decryption of names and message bodies — the same way chat search
 * goes through SDK.getChatMessages.
 *
 * Returns null when the request could not be completed, so the caller can decide
 * whether to fall back to local data rather than showing an empty result set.
 */
const requestLiveGlobalSearch = async ({ searchText, type, page, size, cursor }) => {
    if (typeof SDK.globalSearch !== "function") return null;
    try {
        const response = await SDK.globalSearch({
            searchTerm: searchText,
            type,
            page,
            size,
            ...(cursor ? { cursor } : {})
        });
        if (response?.statusCode !== 200) return null;
        // Re-shape to the { data } envelope the normalizer reads.
        return { status: 200, data: response.data || {} };
    } catch (error) {
        return null;
    }
};

/**
 * Offline stand-in used only when the live request cannot be completed. Searches the
 * data already in the store, so results carry real ids and remain navigable.
 */
const requestStaticGlobalSearch = async ({ searchText, type, page, size, cursor }) => {
    const responseKey = RESPONSE_KEY_BY_TYPE[type];
    const collection = getStoreCollections()[responseKey] || [];
    const matched = collection.filter((item) => matchesSearch(item, type, searchText));

    // Messages paginate on a cursor (the id of the last message already shown),
    // everything else paginates on a page number.
    if (type === GLOBAL_SEARCH_TYPE.MESSAGES) {
        const startIndex = cursor ? matched.findIndex((m) => m.id === cursor) + 1 : 0;
        const slice = matched.slice(startIndex, startIndex + size);
        const consumed = startIndex + slice.length;
        return {
            status: 200,
            data: {
                messages: slice,
                messagesTotalRecords: matched.length,
                messagesHasMore: consumed < matched.length,
                nextMessageCursor: slice.length ? slice[slice.length - 1].id : null
            }
        };
    }

    const startIndex = (page - 1) * size;
    const slice = matched.slice(startIndex, startIndex + size);
    return {
        status: 200,
        data: {
            [responseKey]: slice,
            [`${responseKey}TotalRecords`]: matched.length,
            [`${responseKey}TotalPages`]: Math.ceil(matched.length / size) || 0
        }
    };
};

/**
 * Finds the result array in a response. The documented key is tried first; if the
 * backend names it differently, fall back to the only array present rather than
 * rendering an empty tab. Logs the mismatch so the real key is easy to spot.
 */
const extractItems = (data, responseKey, type) => {
    if (Array.isArray(data[responseKey])) return data[responseKey];

    const arrayKeys = Object.keys(data).filter((key) => Array.isArray(data[key]));
    if (arrayKeys.length === 1) {
        console.warn(
            `[globalSearch] type="${type}" returned "${arrayKeys[0]}" instead of the expected "${responseKey}".`
        );
        return data[arrayKeys[0]];
    }
    if (arrayKeys.length > 1) {
        console.warn(
            `[globalSearch] type="${type}" has no "${responseKey}"; candidates: ${arrayKeys.join(", ")}.`
        );
    }
    return [];
};

/** Pagination field names vary by collection; accept the documented and generic spellings. */
const readNumber = (data, keys) => {
    const key = keys.find((candidate) => typeof data[candidate] === "number");
    return key ? data[key] : 0;
};

/**
 * Reads the per-type items and pagination out of a raw global search response.
 */
const normalizeGlobalSearchResponse = (response, type, page, loadedCount = 0, size = GLOBAL_SEARCH_PAGE_SIZE) => {
    const { data = {} } = response || {};
    const responseKey = RESPONSE_KEY_BY_TYPE[type];
    const items = extractItems(data, responseKey, type);
    // A full page is the most reliable signal that more may exist: backends have been
    // seen to report a wrong totalPages, or omit pagination metadata entirely.
    const isFullPage = items.length >= size;

    // Surfaces exactly which pagination fields came back, so a tab that stops paging
    // early can be diagnosed from the console without guessing at the contract.
    console.debug("[globalSearch] page meta", {
        type,
        page,
        received: items.length,
        size,
        isFullPage,
        paginationFields: Object.fromEntries(
            Object.entries(data).filter(([, value]) => typeof value !== "object")
        )
    });

    if (type === GLOBAL_SEARCH_TYPE.MESSAGES) {
        // Enforce the cap client-side too, so the limit holds regardless of what the
        // backend returns. `capReached` drives the end-of-list notice (MSG102) and is
        // deliberately distinct from simply running out of matches.
        const remaining = Math.max(0, GLOBAL_SEARCH_MESSAGE_CAP - loadedCount);
        const cappedItems = items.slice(0, remaining);
        const totalLoaded = loadedCount + cappedItems.length;
        const nextCursor = data.nextMessageCursor ?? data.nextCursor ?? null;
        const messagesTotal = readNumber(data, ["messagesTotalRecords", "totalRecords", "total"]);
        // Prefer an explicit hasMore; otherwise infer from a full page or the running total.
        // A full page means keep going even if the flag says otherwise; the
        // no-new-results guard in the caller stops it if the server does not advance.
        const serverHasMore = data.messagesHasMore === true || isFullPage || Boolean(nextCursor);
        const moreAvailable = serverHasMore || cappedItems.length < items.length;
        const capReached = totalLoaded >= GLOBAL_SEARCH_MESSAGE_CAP && moreAvailable;

        return {
            items: cappedItems,
            totalRecords: messagesTotal,
            hasMore: moreAvailable && !capReached,
            capReached,
            nextCursor,
            // Messages advance the page as well as the cursor: the backend paginates on
            // page, and sending both lets it use whichever it actually honours.
            nextPage: page + 1
        };
    }

    // Contacts and groups are explicitly uncapped.
    const totalRecords = readNumber(data, [`${responseKey}TotalRecords`, "totalRecords", "total"]);
    const totalPages = readNumber(data, [`${responseKey}TotalPages`, "totalPages"]);
    // A full page wins over the metadata: a wrong or missing totalPages must not be able
    // to stall pagination. A short page ends it, and the caller stops if nothing new comes back.
    const hasMore = isFullPage || (totalPages ? page < totalPages : false);

    return {
        items,
        totalRecords,
        hasMore,
        capReached: false,
        nextCursor: null,
        nextPage: page + 1
    };
};

/**
 * Fetches one page of global search results for a single tab.
 *
 * @param {string} searchText  the term the user typed
 * @param {string} type        one of GLOBAL_SEARCH_TYPE
 * @param {number} page        1-based page, used by recents/contacts/groups
 * @param {number|null} cursor last message id already shown, used by messages only
 * @param {number} loadedCount how many results are already displayed, for the message cap
 * @returns {{ items: Array, hasMore: boolean, capReached: boolean, nextCursor: number|null, nextPage: number|null, totalRecords: number }}
 */
export const fetchGlobalSearch = async ({
    searchText = "",
    type = GLOBAL_SEARCH_TYPE.RECENTS,
    page = 1,
    cursor = null,
    loadedCount = 0,
    size = GLOBAL_SEARCH_PAGE_SIZE
} = {}) => {
    const term = (searchText || "").trim();
    const empty = { items: [], hasMore: false, capReached: false, nextCursor: null, nextPage: null, totalRecords: 0 };
    if (!term) return empty;

    try {
        const liveResponse = await requestLiveGlobalSearch({ searchText: term, type, page, size, cursor });
        if (liveResponse) return normalizeGlobalSearchResponse(liveResponse, type, page, loadedCount, size);

        // The request failed (offline, expired token, endpoint down) — serve what is
        // already in the store so search stays usable instead of looking empty.
        const staticResponse = await requestStaticGlobalSearch({ searchText: term, type, page, size, cursor });
        if (staticResponse?.status !== 200) return empty;
        return normalizeGlobalSearchResponse(staticResponse, type, page, loadedCount, size);
    } catch (error) {
        return empty;
    }
};
